/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { Provider } from 'react-redux';
import store from './redux/store';
import { Amplify, Auth } from "aws-amplify";

import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import App from './App';
import Dashboard from './pages/Dashboard/Dashboard';
import Settings from './pages/Settings/Settings';
import TermsOfService from './pages/TermsOfService/TermsOfService';
import Logout from './pages/Logout/Logout';
import ErrorPage from './pages/ErrorPage/ErrorPage';

import * as serviceWorker from "./serviceWorker";
import awsconfig from "./aws-exports";

import "./index.css";
import { isJapanesePhoneNumber, formatPhoneNumberForJapan } from './utils/phoneNumber';

var Config = require("Config");

let amplifyConfig = {
	...awsconfig,
	Auth: {
		// OPTIONAL - Manually set the authentication flow type. Default is 'USER_SRP_AUTH'
		authenticationFlowType: Config.authenticationFlowType !== undefined ? Config.authenticationFlowType : "USER_SRP_AUTH",
	},
};

// If we have in parameter that means start a PKCE or Implict flow
// We switch the clientId to submit the one from the client website
let queryStringParams = new URLSearchParams(window.location.search);
let clientId = queryStringParams.get("client_id");
if (clientId) {
	// We save the client ID, our Amplify auth will be based on that one
	localStorage.setItem("client-id", clientId);
} else {
	// If there is no client-Id in query, we set back the last one used for login
	// it may be undefined if we never logged in
	clientId = localStorage.getItem("client-id");
}
if (clientId) {
	// We configure the Amplify Auth component in the context of using a client website client-id
	// if no clientId is found (direct login not from a client) the web client id of the broker will be used as default
	amplifyConfig.aws_user_pools_web_client_id = clientId;
}
Amplify.configure(amplifyConfig);

// Login Alert API call
const callLoginAlertApi = async (userId, email, phoneNumber, loginUsername) => {
	try {
		// Get API endpoint from aws-exports
		const apiConfig = awsconfig.aws_cloud_logic_custom?.find(
			api => api.name === 'amplifyIdentityBrokerApi'
		);

		if (!apiConfig) {
			console.warn('Login alert API endpoint not configured');
			return;
		}

		const endpoint = `${apiConfig.endpoint}/loginAlert`;

		// ログイン種別の判定
		// - @を含む場合はメールアドレスログイン
		// - +81で始まる or 数字のみの場合は電話番号ログイン
		let loginMethod = 'unknown';
		if (loginUsername) {
			if (loginUsername.includes('@')) {
				loginMethod = 'email';
			} else if (loginUsername.startsWith('+81') || /^[0-9]+$/.test(loginUsername)) {
				loginMethod = 'phone';
			}
		}

		const response = await fetch(endpoint, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				userId,
				email,
				phoneNumber,
				loginMethod
			})
		});

		const result = await response.json();

		if (result.alerts && result.alerts.length > 0) {
			console.log('Login alerts detected:', result.alerts);
			// アラートはログに記録のみ（画面表示なし）
			// 通知はLambda側でSendGrid/SMS経由で送信される
		}
	} catch (error) {
		// ログインアラートAPIのエラーはログイン自体に影響させない
		console.error('Login alert API error:', error);
	}
};

// Wrap Auth.signIn to handle phone number formatting and login alert
const originalSignIn = Auth.signIn.bind(Auth);
Auth.signIn = async (usernameOrOptions, password, validationData) => {
	let username = usernameOrOptions;

	// Handle both signIn(username, password) and signIn({ username, password }) formats
	if (typeof usernameOrOptions === 'object') {
		const originalUsername = usernameOrOptions.username;
		if (usernameOrOptions.username && isJapanesePhoneNumber(usernameOrOptions.username)) {
			usernameOrOptions.username = formatPhoneNumberForJapan(usernameOrOptions.username);
		}
		const result = await originalSignIn(usernameOrOptions);

		// ログイン成功後にログインアラートAPIを呼び出す
		if (result && result.attributes) {
			callLoginAlertApi(
				result.attributes.sub || result.username,
				result.attributes.email,
				result.attributes.phone_number,
				originalUsername
			);
		}

		return result;
	}

	// Format phone number if it's a Japanese local number
	const originalUsername = username;
	if (isJapanesePhoneNumber(username)) {
		username = formatPhoneNumberForJapan(username);
	}

	const result = await originalSignIn(username, password, validationData);

	// ログイン成功後にログインアラートAPIを呼び出す
	if (result && result.attributes) {
		callLoginAlertApi(
			result.attributes.sub || result.username,
			result.attributes.email,
			result.attributes.phone_number,
			originalUsername
		);
	}

	return result;
};

// Wrap Auth.forgotPassword to handle phone number formatting
const originalForgotPassword = Auth.forgotPassword.bind(Auth);
Auth.forgotPassword = async (username, clientMetadata) => {
	// Format phone number if it's a Japanese local number
	if (isJapanesePhoneNumber(username)) {
		username = formatPhoneNumberForJapan(username);
	}
	return originalForgotPassword(username, clientMetadata);
};

// Wrap Auth.forgotPasswordSubmit to handle phone number formatting
const originalForgotPasswordSubmit = Auth.forgotPasswordSubmit.bind(Auth);
Auth.forgotPasswordSubmit = async (username, code, newPassword, clientMetadata) => {
	// Format phone number if it's a Japanese local number
	if (isJapanesePhoneNumber(username)) {
		username = formatPhoneNumberForJapan(username);
	}
	return originalForgotPasswordSubmit(username, code, newPassword, clientMetadata);
};

/*
 * App Routing
 * With "ProtectedRoute" we redirect user to Login if they are not signed in and _
 * we redirect user to Terms of Service (ToS) if a user not accepted the current ToS
 * 
 * If we route to a none existing page we will redirect to the "ErrorPage"
 */
ReactDOM.render(
	<React.StrictMode>
		<Provider store={store}>
			<Router>
				<Switch>
					<ProtectedRoute exact path="/dashboard" component={Dashboard} />
					<ProtectedRoute exact path="/settings/password" component={Settings} />
					<ProtectedRoute exact path="/settings/email" component={Settings} />
					<ProtectedRoute exact path="/settings/phone" component={Settings} />
					<ProtectedRoute exact path="/settings" component={Settings} />
					<Route exact path="/tos" component={TermsOfService} />
					<Route exact path="/logout" component={Logout} />
					<Route exact path="/" component={App} />
					<Route component={ErrorPage} />
				</Switch>
			</Router>
		</Provider>
	</React.StrictMode>,
	document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://create-react-app.dev/docs/making-a-progressive-web-app
serviceWorker.unregister();
